import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { daysUntilExpiryDay, parseExpiryDay } from "../utils/license-expiry";
import "./useSubscriptionExpiryReminder.scss";

const SUBSCRIPTION_TOAST_LOGIN_FLAG = "_show_subscription_expiry_toast";

interface ExpiryStatus {
  dateStr: string;
  diffDays: number;
}

const parseExpiryStatus = (expiryInput: string): ExpiryStatus | null => {
  // The backend sends a date only, so this is parsed as local midnight and
  // compared at day granularity — see utils/license-expiry.
  const expiryDay = parseExpiryDay(expiryInput);
  if (!expiryDay) return null;

  const diffDays = daysUntilExpiryDay(expiryInput);
  if (diffDays === null) return null;

  const dd = String(expiryDay.getDate()).padStart(2, "0");
  const mm = String(expiryDay.getMonth() + 1).padStart(2, "0");
  const yyyy = expiryDay.getFullYear();

  return {
    dateStr: `${dd}-${mm}-${yyyy}`,
    diffDays,
  };
};

const buildReminderMessage = (status: ExpiryStatus): string | null => {
  if (status.diffDays < 0) {
    return `Your subscription expired on ${status.dateStr}. Please renew to continue uninterrupted access.`;
  }

  if (status.diffDays === 0) {
    return "Your subscription expires today. Please renew to avoid service interruption.";
  }

  if (status.diffDays <= 5) {
    return `Your subscription expires in ${status.diffDays} day${
      status.diffDays === 1 ? "" : "s"
    } (${status.dateStr}). Please renew soon.`;
  }

  return null;
};

export const markSubscriptionReminderForLogin = () => {
  sessionStorage.setItem(SUBSCRIPTION_TOAST_LOGIN_FLAG, "1");
};

export function useSubscriptionExpiryReminder() {
  const { isAuthenticated } = useSelector(({ auth }: any) => auth);
  const licenseExpiryDt = useSelector(
    ({ metadata }: any) => metadata?.dashboardState?.licenseExpiryDt
  );
  const hasShownForCurrentLoginRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) {
      hasShownForCurrentLoginRef.current = false;
      return;
    }

    const shouldShowForThisLogin =
      sessionStorage.getItem(SUBSCRIPTION_TOAST_LOGIN_FLAG) === "1";

    if (!shouldShowForThisLogin || hasShownForCurrentLoginRef.current) {
      return;
    }

    if (!licenseExpiryDt) {
      return;
    }

    const expiryStatus = parseExpiryStatus(licenseExpiryDt);
    if (!expiryStatus) {
      hasShownForCurrentLoginRef.current = true;
      sessionStorage.removeItem(SUBSCRIPTION_TOAST_LOGIN_FLAG);
      return;
    }

    const reminderMessage = buildReminderMessage(expiryStatus);
    hasShownForCurrentLoginRef.current = true;
    sessionStorage.removeItem(SUBSCRIPTION_TOAST_LOGIN_FLAG);

    if (!reminderMessage) {
      return;
    }

    toast.custom(
      (t) => (
        <div className="subscription-reminder-toast" role="alert" aria-live="assertive">
          <span className="subscription-reminder-toast__icon" aria-hidden="true">⚠</span>
          <div className="subscription-reminder-toast__content">
            <div className="subscription-reminder-toast__title">Subscription Reminder</div>
            <div className="subscription-reminder-toast__message">{reminderMessage}</div>
          </div>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              toast.dismiss(t.id);
            }}
            aria-label="Close subscription reminder"
            className="subscription-reminder-toast__close"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            >
              <line x1="4" y1="4" x2="16" y2="16" />
              <line x1="16" y1="4" x2="4" y2="16" />
            </svg>
          </button>
        </div>
      ),
      {
        duration: 10000,
        position: "top-right",
      }
    );
  }, [isAuthenticated, licenseExpiryDt]);
}
